import {
  BaseTransport,
  ConnectionError,
  DisconnectionError,
  getLogger,
  SendError,
  ValidationError,
} from "@eventmsg/core";
import type { WebBLETransportConfig } from "./types/index.js";
import {
  WEB_BLUETOOTH_ERROR_MAP,
  WEB_BLUETOOTH_ERROR_MESSAGES,
} from "./types/index.js";

// UUID validation regex
const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * WebBLE Transport for EventMsgV3 protocol
 *
 * Provides Web Bluetooth connectivity for browser-based communication
 * with ESP32 devices using BLE/GATT protocol.
 *
 * Features:
 * - Nordic UART Service support by default
 * - Automatic data chunking for BLE MTU limitations
 * - Device caching and automatic reconnection
 * - Browser compatibility checks
 * - Comprehensive error handling
 */
export class WebBLETransport extends BaseTransport<
  WebBLETransportConfig,
  BluetoothDevice
> {
  private readonly logger = getLogger("TRANSPORT_WEBBLE");
  private server: BluetoothRemoteGATTServer | undefined;
  private service: BluetoothRemoteGATTService | undefined;
  private rxCharacteristic: BluetoothRemoteGATTCharacteristic | undefined;
  private txCharacteristic: BluetoothRemoteGATTCharacteristic | undefined;

  private mtu = 480;
  private deviceCache: string | null = null;

  private receiveBuffer = new Uint8Array(0);
  private readonly MAX_MESSAGE_SIZE = 4096;
  private bufferTimeout?: NodeJS.Timeout | undefined;

  constructor(config: WebBLETransportConfig) {
    super(config);

    this.checkBrowserSupport();
    this.config.connection = {
      timeout: 10_000,
      persistDevice: true,
      reconnectAttempts: 3,
      reconnectDelay: 1000,
      ...this.config.connection,
    };
  }

  /**
   * Check if Web Bluetooth is supported in current browser
   * @throws {ValidationError} If Web Bluetooth is not supported
   */
  private checkBrowserSupport(): void {
    if (!("bluetooth" in navigator)) {
      throw new ValidationError(
        "Web Bluetooth API not supported in this browser",
        {
          context: {
            ...this.getErrorContext("browser_check"),
            solution:
              "Use Chrome, Edge, or Opera browser with Web Bluetooth support",
          },
        }
      );
    }

    if (!window.isSecureContext) {
      throw new ValidationError(
        "Web Bluetooth requires a secure context (HTTPS)",
        {
          context: {
            ...this.getErrorContext("browser_check"),
            solution: "Use HTTPS or localhost for development",
          },
        }
      );
    }
  }

  /**
   * Validate WebBLE-specific configuration
   * @param config Configuration to validate
   * @throws {ValidationError} If configuration is invalid
   */
  protected validateConfig(config: WebBLETransportConfig): void {
    // Call parent validation for base config
    super.validateConfig(config);

    // Validate service configuration
    if (!config.service) {
      throw new ValidationError("Service configuration is required", {
        context: this.getErrorContext("config_validation"),
      });
    }

    if (!config.service.uuid) {
      throw new ValidationError("Service UUID is required", {
        context: this.getErrorContext("config_validation"),
      });
    }

    if (!config.service.txCharacteristic) {
      throw new ValidationError("TX characteristic UUID is required", {
        context: this.getErrorContext("config_validation"),
      });
    }

    if (!config.service.rxCharacteristic) {
      throw new ValidationError("RX characteristic UUID is required", {
        context: this.getErrorContext("config_validation"),
      });
    }

    // Validate UUIDs format (basic check)
    if (!UUID_REGEX.test(config.service.uuid)) {
      throw new ValidationError("Invalid service UUID format", {
        context: {
          ...this.getErrorContext("config_validation"),
          serviceUuid: config.service.uuid,
        },
      });
    }

    if (!UUID_REGEX.test(config.service.txCharacteristic)) {
      throw new ValidationError("Invalid TX characteristic UUID format", {
        context: {
          ...this.getErrorContext("config_validation"),
          txCharacteristic: config.service.txCharacteristic,
        },
      });
    }

    if (!UUID_REGEX.test(config.service.rxCharacteristic)) {
      throw new ValidationError("Invalid RX characteristic UUID format", {
        context: {
          ...this.getErrorContext("config_validation"),
          rxCharacteristic: config.service.rxCharacteristic,
        },
      });
    }
  }

  /**
   * Transport-specific connection implementation
   * Handles Web Bluetooth device selection and GATT connection
   */
  protected async doConnect(): Promise<void> {
    try {
      this.device = await this.getOrRequestDevice();
      if (!this.device.gatt) {
        throw new Error("Device does not support GATT");
      }

      this.server = await this.device.gatt.connect();
      this.service = await this.server.getPrimaryService(
        this.config.service.uuid
      );
      this.rxCharacteristic = await this.service.getCharacteristic(
        this.config.service.rxCharacteristic
      );
      this.txCharacteristic = await this.service.getCharacteristic(
        this.config.service.txCharacteristic
      );
      await this.txCharacteristic.startNotifications();
      this.txCharacteristic.addEventListener(
        "characteristicvaluechanged",
        this.handleIncomingData.bind(this)
      );
      this.device.addEventListener(
        "gattserverdisconnected",
        this.handleGATTDisconnect.bind(this)
      );

      this.negotiateMTU();
      if (this.config.connection?.persistDevice) {
        this.cacheDevice();
      }
      this.logger.info("WebBLE connected successfully", {
        deviceName: this.device?.name || "Unknown",
        deviceId: this.device?.id,
        mtu: this.mtu,
        serviceUuid: this.config.service.uuid,
        rxCharacteristic: this.config.service.rxCharacteristic,
        txCharacteristic: this.config.service.txCharacteristic,
      });
    } catch (error) {
      await this.cleanupConnection();
      throw this.wrapBLEError(error, "connection");
    }
  }

  /**
   * Transport-specific disconnection implementation
   * Cleans up BLE resources and disconnects
   */
  protected async doDisconnect(): Promise<void> {
    try {
      this.logger.info("WebBLE disconnecting", {
        deviceName: this.device?.name || "Unknown",
        connected: this.server?.connected,
      });
      await this.cleanupConnection();
      this.logger.info("WebBLE disconnected successfully");
    } catch (error) {
      throw this.wrapBLEError(error, "disconnection");
    }
  }

  /**
   * Transport-specific send implementation
   * Sends data via BLE characteristic with automatic chunking
   */
  protected async doSend(data: Uint8Array): Promise<void> {
    if (!this.rxCharacteristic) {
      throw new SendError("RX characteristic not available", {
        context: this.getErrorContext("send"),
      });
    }

    try {
      // Chunk data for BLE MTU limitations
      const chunks = this.chunkData(data);

      this.logger.debug("Sending message via WebBLE", {
        totalSize: data.length,
        chunkCount: chunks.length,
        mtu: this.mtu,
      });

      // Send chunks sequentially (required for BLE reliability)
      // Note: BLE often requires sequential sending for proper ordering
      for (const chunk of chunks) {
        await this.sendChunk(chunk);
      }

      this.logger.debug("Message sent successfully", {
        totalSize: data.length,
        chunksSent: chunks.length,
      });
    } catch (error) {
      throw this.wrapBLEError(error, "send");
    }
  }

  /**
   * Get cached device or request new device from user
   */
  private getOrRequestDevice(): Promise<BluetoothDevice> {
    // Try cached device first
    if (this.config.connection?.persistDevice && this.deviceCache) {
      // TODO: Implement device cache retrieval
      // This would require storing device ID and attempting reconnection
      // For now, just clear cache and request new device
      this.deviceCache = null;
    }

    // Request new device from user
    return this.requestDevice();
  }

  /**
   * Request device selection from user
   */
  private async requestDevice(): Promise<BluetoothDevice> {
    const filters = this.config.filters || [
      { services: [this.config.service.uuid] },
    ];

    try {
      const device = await navigator.bluetooth.requestDevice({
        filters,
        optionalServices: [this.config.service.uuid],
      });

      return device;
    } catch (error) {
      throw this.wrapBLEError(error, "device_request");
    }
  }

  /**
   * Handle incoming data from BLE characteristic
   */
  private handleIncomingData(event: Event): void {
    const characteristic = event.target as BluetoothRemoteGATTCharacteristic;
    const value = characteristic.value;

    if (!value) {
      return;
    }

    // Convert DataView to Uint8Array
    const chunk = new Uint8Array(value.buffer);

    this.logger.debug("WebBLE chunk received", {
      chunkSize: chunk.length,
      currentBufferSize: this.receiveBuffer.length,
      newBufferSize: this.receiveBuffer.length + chunk.length,
    });

    // Simple concatenation
    const newBuffer = new Uint8Array(this.receiveBuffer.length + chunk.length);
    newBuffer.set(this.receiveBuffer);
    newBuffer.set(chunk, this.receiveBuffer.length);
    this.receiveBuffer = newBuffer;

    // Safety check for malformed data
    if (this.receiveBuffer.length > this.MAX_MESSAGE_SIZE) {
      this.logger.warn("Message too large, clearing buffer", {
        bufferSize: this.receiveBuffer.length,
        maxSize: this.MAX_MESSAGE_SIZE,
        chunkSize: chunk.length,
      });
      this.clearReceiveBuffer();
      return;
    }

    // Reset timeout on each chunk
    this.resetBufferTimeout();

    // Check if we have a complete message (starts with SOH, ends with EOT)
    if (
      this.receiveBuffer.length >= 10 && // Minimum valid message size
      this.receiveBuffer[0] === 0x01 && // SOH
      this.receiveBuffer.at(-1) === 0x04
    ) {
      // EOT

      this.logger.info("Message reassembled successfully", {
        finalSize: this.receiveBuffer.length,
        chunksReceived:
          this.receiveBuffer.length <= 500
            ? 1
            : Math.ceil(this.receiveBuffer.length / 500),
      });

      // Emit complete message
      this.emit("data", this.receiveBuffer);

      // Clear buffer for next message
      this.clearReceiveBuffer();
    } else {
      // Set timeout to clear stale buffer after 5 seconds
      this.bufferTimeout = setTimeout(() => {
        if (this.receiveBuffer.length > 0) {
          this.logger.warn("Partial message timeout, clearing buffer", {
            bufferSize: this.receiveBuffer.length,
            timeoutMs: 5000,
            hasSOH: this.receiveBuffer[0] === 0x01,
            hasEOT: this.receiveBuffer.at(-1) === 0x04,
          });
          this.clearReceiveBuffer();
        }
      }, 5000);
    }
  }

  /**
   * Clear receive buffer and timeout
   */
  private clearReceiveBuffer(): void {
    this.receiveBuffer = new Uint8Array(0);
    this.resetBufferTimeout();
  }

  /**
   * Reset buffer timeout
   */
  private resetBufferTimeout(): void {
    if (this.bufferTimeout) {
      clearTimeout(this.bufferTimeout);
      this.bufferTimeout = undefined;
    }
  }

  /**
   * Handle GATT server disconnection event
   */
  private handleGATTDisconnect(_event: Event): void {
    this.handleUnexpectedDisconnect();
  }

  /**
   * Negotiate MTU size with device
   */
  private negotiateMTU(): void {
    // Web Bluetooth doesn't provide direct MTU negotiation
    // Use conservative default for now
    this.mtu = this.config.connection?.mtu || 20;
  }

  /**
   * Cache device for reconnection
   */
  private cacheDevice(): void {
    if (this.device) {
      this.deviceCache = this.device.id;
    }
  }

  /**
   * Split data into chunks that fit within BLE MTU
   */
  private chunkData(data: Uint8Array): Uint8Array[] {
    const chunkSize = this.mtu;
    const chunks: Uint8Array[] = [];

    for (let i = 0; i < data.length; i += chunkSize) {
      chunks.push(data.slice(i, i + chunkSize));
    }

    return chunks;
  }

  /**
   * Send a single data chunk
   */
  private async sendChunk(chunk: Uint8Array): Promise<void> {
    if (!this.rxCharacteristic) {
      throw new Error("RX characteristic not available");
    }

    try {
      await this.rxCharacteristic.writeValueWithResponse(chunk as BufferSource);
    } catch (error) {
      this.logger.error("BLE chunk send failed", {
        error: error instanceof Error ? error.message : String(error),
        chunkSize: chunk.length,
        cause: error instanceof Error ? error.name : "Unknown",
      });
      throw error; // Throw original error
    }
  }

  /**
   * Clean up BLE connection resources
   */
  private async cleanupConnection(): Promise<void> {
    // Clear receive buffer and timeout
    this.clearReceiveBuffer();

    // Stop notifications
    if (this.txCharacteristic) {
      try {
        await this.txCharacteristic.stopNotifications();
      } catch {
        // Ignore errors during cleanup
      }
      this.txCharacteristic = undefined;
    }

    // Clear characteristics
    this.rxCharacteristic = undefined;
    this.service = undefined;

    // Disconnect GATT server
    if (this.server?.connected) {
      this.server.disconnect();
    }
    this.server = undefined;

    this.device = null;
  }

  /**
   * Convert Web Bluetooth errors to transport errors
   */
  private wrapBLEError(error: unknown, operation: string): Error {
    if (error instanceof Error) {
      // Map known Web Bluetooth errors
      const bleErrorCode = WEB_BLUETOOTH_ERROR_MAP[error.name];

      if (bleErrorCode) {
        const errorInfo = WEB_BLUETOOTH_ERROR_MESSAGES[bleErrorCode];

        // Choose appropriate transport error type
        if (operation === "connection") {
          return new ConnectionError(errorInfo.message, {
            cause: error,
            context: {
              ...this.getErrorContext(operation),
              solution: errorInfo.solution,
            },
          });
        }
        if (operation === "disconnection") {
          return new DisconnectionError(errorInfo.message, {
            cause: error,
            context: {
              ...this.getErrorContext(operation),
              solution: errorInfo.solution,
            },
          });
        }
        return new SendError(errorInfo.message, {
          cause: error,
          context: {
            ...this.getErrorContext(operation),
            solution: errorInfo.solution,
          },
        });
      }
    }

    // Fallback for unknown errors
    const message = error instanceof Error ? error.message : String(error);
    return new ConnectionError(`BLE ${operation} failed: ${message}`, {
      cause: error instanceof Error ? error : new Error(String(error)),
      context: this.getErrorContext(operation),
    });
  }
}
